import React, { Component, createRef } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { get } from 'lodash-es';
import { Button, Container } from 'reactstrap';
import ReactLoading from 'react-loading';

import AddLotDropzone from './AddLotDropzone';
import UnusedLotData from './UnusedLotData';
import InlineAlert from '../../../utilities/form-components/InlineAlert';
import validateLotForm from '../diy-utils/validateLotForm';
import LotForm from '../diy-forms/LotForm';

class AddLot extends Component {
  constructor(props) {
    super(props);

    /**
     * Add a reference in the JSX we can scroll to when the user submits add lot.
     *
     * Essentially the dropzone needs to remain hidden until after the user submits lot
     * information.  This allows us to transgress into the 'edit lot' permutation from the
     * 'add lot' permutation.
     */
    this.myRef = createRef();

    this.state = {
      error: null,
      errorDeletedLot: false,
      invNumberArray: [],
      lotNumberArray: [],
      loading: false,
      lotData: {
        artistName: '',
        circa: '',
        condition: '',
        dimensions: '',
        exhibited: '',
        hiEst: '',
        invNumber: '',
        itemPrice: '',
        literature: '',
        loEst: '',
        lotDescription: '',
        lotExt: '',
        lotNumber: '',
        lotTitle: '',
        medium: '',
        notes: '',
        provenance: '',
        reserve: '',
        shipHeight: '',
        shipLength: '',
        shipWeight: '',
        shipWidth: '',
        shipping: '',
        shippingIntl: '',
        startingBid: ''
      },
      lotImages: [],
      orderBids: [],
      showDeletedLotError: false,
      showSubmitError: false,
      validation: {
        lotNumber: null,
        lotExt: null,
        lotTitle: null,
        lotDescription: null,
        shipWidth: null,
        shipHeight: null,
        shipLength: null,
        shipWeight: null,
        shipping: null,
        shippingIntl: null,
        loEst: null,
        hiEst: null,
        startingBid: null,
        invNumber: null,
        reserve: null
      }
    };
  }

  async componentDidMount() {
    const {
      house,
      match: {
        params: { catalogID, lotNumber: lotIdParam }
      }
    } = this.props;

    try {
      this.setState({ loading: true });

      const isEditing = lotIdParam !== '0';

      if (isEditing) {
        // Re-sequence all the images first in case the user uploaded them as like 1, 5, 25
        try {
          await axios(`/cps/app/catalog/${catalogID}/${lotIdParam}/resequenceimages`);
        } catch (error) {
          this.setState({ error, loading: false });
        }
      }

      /**
       * Preview API should be faster than getting all the extra crap from the other lots,
       * since we only need the lot number list and inventory number list
       */
      const requestChain = [
        axios(`/cps/app/catalog/${catalogID}/orderbids`),
        axios(`/cps/app/catalog/${catalogID}/preview`),
        axios(`/cps/app/catalog/${catalogID}/deletedlots`)
      ];

      // In the edit session - get all the lot details
      if (isEditing) {
        requestChain.push(axios(`/cps/app/catalog/${catalogID}/${lotIdParam}`));
        requestChain.push(
          axios(`/cps/app/catalog/${catalogID}/${lotIdParam}/house/${house.houseID}/getimageurls`)
        );
      }

      const [
        orderBidResponse,
        lotResponse,
        deletedLotResponse,
        lotDataResponse,
        lotImageResponse
      ] = await Promise.all(requestChain);

      const orderBids = get(orderBidResponse, 'data.data.orderbids', null);
      const lots = get(lotResponse, 'data.data.lots', null);
      const deletedLotsArray = get(deletedLotResponse, 'data.data.deletedLotsArray', []);
      const lotNumberArray = lots.map(lot => lot.lotNumber + lot.lotExt);
      const invNumberArray = lots.map(lot => lot.invNumber);

      if (lotDataResponse) {
        const lotData = get(lotDataResponse, 'data.data.lots[0]', []);
        const { lotNumber, lotExt, invNumber, lotID } = lotData;
        const lotNumberExt = `${lotNumber + lotExt}`;
        lotData.hasLotNumber = (lotNumber);
        const lotImages = get(lotImageResponse, `data.data[${lotID}]`, []);

        /**
         * Remove the existing inventory number / lot number from the array in case the
         * user is editing and wants to keep their existing lot number / inventory number.
         */
        const lotNumberIndex = lotNumberArray.findIndex(lotNum => lotNum === lotNumberExt);
        const invNumberIndex = invNumberArray.findIndex(invNum => invNum === invNumber);
        lotNumberArray.splice(lotNumberIndex, 1);
        invNumberArray.splice(invNumberIndex, 1);

        this.setState({
          deletedLotsArray,
          invNumberArray,
          lotData,
          lotImages,
          lotNumberArray,
          loading: false,
          orderBids
        });
      } else {
        this.setState({
          deletedLotsArray,
          orderBids,
          invNumberArray,
          lotNumberArray,
          loading: false
        });
      }
    } catch (error) {
      const errorURL = error.request.responseURL;
      if (errorURL.indexOf('getimageurls') >= 0) {
        this.setState({ errorDeletedLot: true, loading: false });
      } else {
        this.setState({ error, loading: false });
      }
    }
  }

  // Dynamically set changed input fields in the state
  handleInputChange = e => {
    const { id, value } = e.target;
    const { validation } = this.state;

    // Clear validation until the user exits the field
    delete validation[id];

    this.setState({
      lotData: {
        ...this.state.lotData,
        [id]: value
      },
      validation
    });
  };

  submitLot = async () => {
    const {
      location: { state },
      match: {
        params: { catalogID, groupID, lotNumber: lotNumberParam, type, goToPage }
      }
    } = this.props;
    const { lotData } = this.state;

    const isBin = type.toLowerCase() === 'bin';
    const isAddingLot = lotNumberParam === '0';

    // Add BIN property
    lotData.isBinLot = isBin;

    // UnusedLotData needs to be appended if we are adding a lot or else CPS gets angry
    const postData = { ...lotData, ...UnusedLotData };

    // CPS needs BIN value as itemPrice - even though it may return it to us as startingBid
    if (isBin) {
      delete postData.startingBid;
    } else {
      // FE workaround to handle weird CPS behavior which changes startingBid values for some reason
      delete postData.itemPrice;
    }

    try {
      const addLotPost = await axios.post(
        `/cps/app/catalog/${catalogID}/lot/${isAddingLot ? 'new' : 'update'}`,
        postData,
        {
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          }
        }
      );
      // only if Invavluable Shipping is enabled
      if (this.props.policy?.id) {
        try {
          await axios.post(`/cps/app/catalog/${catalogID}/shipping-review`, isAddingLot ? {} :
          {
            lotIDs: [this.props.match.params.lotNumber]
          },
          {
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
            }
          }
        );
        } catch (err) {
          console.log('Shipping review error update failed with ', err)
        }
      }
      const baseUrl = `/session/${type}/${parseInt(groupID, 10)}/${parseInt(catalogID, 10)}`;
      if (isAddingLot) {
        const lotID = addLotPost.data.data.lots[0].lotID;
        this.props.history.push(`${baseUrl}/lot/${lotID}`);

        // Bring user to the upload images dropzone
        window.scrollTo({
          top: this.myRef.current.offsetTop,
          behavior: 'smooth'
        });
      } else {
        const linkedFrom = get(state, 'linkedFrom', null);
        const { stepNumber } = this.getStepTrackerInfo(linkedFrom, isBin);
        const redirectPage = goToPage ? `/${goToPage}` : '';
        this.props.history.push(`${baseUrl}/${stepNumber}${redirectPage}`);
      }
    } catch (err) {
      window.scrollTo(0, 0);
      const respErrors = get(err, 'response.data.errors', []);
      const hasDeletedLotError = respErrors.filter(error => error.errorCode === 112).length > 0;
      if (hasDeletedLotError) {
        this.setState({ showDeletedLotError: true });
      } else {
        this.setState({ showSubmitError: true });
      }
    }
  };

  validateFields = fieldName => {
    const {
      match: {
        params: { type }
      },
      house,
    } = this.props;
    const { deletedLotsArray, invNumberArray, lotData, lotNumberArray, validation } = this.state;

    // validate user missing the correct connectType (platinum (e) or gold (p))
    const ycConnectTypes = ['e', 'p'];
    const isGoldPlatinum = ycConnectTypes.indexOf(house.connectType) !== -1;

    this.setState({
      validation: validateLotForm(
        new Set(deletedLotsArray),
        fieldName,
        invNumberArray,
        lotData,
        lotNumberArray,
        type,
        validation,
        isGoldPlatinum,
      )
    });
  };

  getStepTrackerInfo = (linkedFrom, isBin) => {
    let stepName = '';
    let stepNumber = '';
    if (isBin) {
      stepName = linkedFrom === 'step4' ? 'Preview' : 'Summary';
      stepNumber = linkedFrom === 'step4' ? 4 : 5;
    } else {
      stepName = linkedFrom === 'step5' ? 'Preview' : 'Summary';
      stepNumber = linkedFrom === 'step5' ? 5 : 6;
    }

    return { stepName, stepNumber };
  };

  render() {
    const { error, errorDeletedLot, loading } = this.state;
    const {
      location: { state },
      match: {
        params: { catalogID, groupID, lotNumber, type, goToPage }
      },
      house,
    } = this.props;

    // validate user missing the correct connectType (platinum (e) or gold (p))
    const ycConnectTypes = ['e', 'p'];
    const isGoldPlatinum = ycConnectTypes.indexOf(house.connectType) !== -1;

    if (!catalogID || error) {
      return (
        <Container className="py-4">
          <InlineAlert />
        </Container>
      );
    }

    if (loading) {
      return <ReactLoading className="loading-spinner" type="spin" color="black" />;
    }

    const linkedFrom = get(state, 'linkedFrom', null);
    const isBin = type.toLowerCase() === 'bin';

    const { stepName, stepNumber } = this.getStepTrackerInfo(linkedFrom, isBin);

    if (errorDeletedLot) {
      return (
        <Container className="py-4">
          <InlineAlert
            titleText="Lot Deleted"
            baseText="This lot has been deleted and can no longer be edited"
            showPhones={false}
          />
          <Link
            to={`/session/${type}/${parseInt(groupID, 10)}/${parseInt(
              catalogID,
              10
            )}/${stepNumber}`}
          >
            {`Click Here to Return to ${stepName}`}
          </Link>
        </Container>
      );
    }

    const {
      lotData,
      lotImages,
      orderBids,
      showDeletedLotError,
      showSubmitError,
      validation
    } = this.state;

    const isEditing = lotNumber !== '0';
    const addOrEditText = isEditing ? 'Edit' : 'Add';

    /**
     * Disable the save button if there are validation errors, or if
     * the user is missing required fields
     */
    const { lotDescription, lotNumber: lotNumberFromForm, lotTitle } = lotData;
    const lotN = isGoldPlatinum ? false : !lotNumberFromForm;
    let missingRequiredFields = !lotDescription || lotN || !lotTitle;

    if (isBin) {
      const { itemPrice } = lotData;
      missingRequiredFields = missingRequiredFields || !itemPrice;
    }

    const disableSubmit = Object.keys(validation).some(key => validation[key])
      || missingRequiredFields;

    const hasOrderBids = orderBids.some(item => item === lotData.lotNumber + lotData.lotExt);

    const redirectPage = goToPage ? goToPage : '';

    return (
      <Container className="py-4">
        <h1 className="mb-1">{addOrEditText} Lot</h1>
        <p>* = Required</p>

        {showDeletedLotError && (
          <InlineAlert
            titleText="Previously Deleted Lot Number"
            baseText={`Lot Number ${lotNumberFromForm}${lotData.lotExt} was used on a previously deleted lot.  Please enter a new lot number.`}
            showPhones={false}
          />
        )}

        {showSubmitError && (
          <InlineAlert
            baseText={`Unable to ${addOrEditText.toLowerCase()} your lot. Please try again later or contact Customer Care any time at:`}
          />
        )}

        <LotForm
          handleInputChange={this.handleInputChange}
          hasOrderBids={hasOrderBids}
          isEditing={isEditing}
          lotData={lotData}
          type={type}
          validateFields={this.validateFields}
          validation={validation}
        />

        {/* Needed in order to use a scroll to when submitting Add Lot */}
        <div className="mb-3" ref={this.myRef} />
        {isEditing && lotData && <AddLotDropzone houseID={house.houseID} lotImages={lotImages} lotData={lotData} />}

        <div className="mt-4">
          <Button
            className={`${disableSubmit ? 'cursor-not-allowed' : ''} min-width-max-content`}
            color="primary"
            disabled={disableSubmit}
            role="link"
            onClick={() => this.submitLot()}
          >
            {!isEditing ? 'Save & Add Images' : `Save & Return to ${stepName}`}
          </Button>

          <Link
            className="ml-3"
            to={`/session/${type}/${parseInt(groupID, 10)}/${parseInt(
              catalogID,
              10
            )}/${stepNumber}`}
          >
            {`Cancel & Return to ${stepName}`}
          </Link>
        </div>
      </Container>
    );
  }
}

const mapStateToProps = ({ house, policy }) => ({
  house,
  policy,
});

export default connect(
  mapStateToProps,
  null
)(AddLot);
